import React, { useState, useEffect } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { Fragment } from "react";
import {
  XMarkIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  AcademicCapIcon,
  BeakerIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import ScoreInput from "./ScoreInput";
import { getProgrammeTypeDisplayName } from "../utils/accreditationRules";

/**
 * TemplateScoreModal Component
 * Full-screen modal for scoring template items
 *
 * @param {boolean} isOpen - Whether modal is open
 * @param {function} onClose - Close handler
 * @param {object} template - Template object with items
 * @param {object} savedScores - Saved scores object { itemId: score }
 * @param {function} onSave - Save handler (isDraft: boolean, scores: object)
 * @param {string} programmeId - Programme ID
 * @param {string} accreditationId - Accreditation ID
 * @param {string} programmeType - Programme type (UG, PG, ODL)
 * @param {boolean} isLoading - Loading state
 * @param {boolean} isReadOnly - Read-only mode (stage 6 - submitted)
 */
const TemplateScoreModal = ({
  isOpen,
  onClose,
  template,
  savedScores = {},
  onSave,
  programmeId,
  accreditationId,
  programmeType = "UG",
  isLoading = false,
  isReadOnly = false,
}) => {
  const [scores, setScores] = useState({});
  const [naFlags, setNaFlags] = useState({});
  const [errors, setErrors] = useState({});
  const [totalScore, setTotalScore] = useState(0);
  const [maxPossibleScore, setMaxPossibleScore] = useState(0);

  const isCore = template?.type === "core";
  const items = template?.items || [];
  const programmeTypeDisplay = getProgrammeTypeDisplayName(programmeType);

  // Initialize scores from savedScores when modal opens
  useEffect(() => {
    if (isOpen) {
      console.log('[TemplateScoreModal] Initializing with savedScores:', savedScores);

      // Handle both nested and flat score structures defensively
      let initialScores = {};

      if (savedScores && typeof savedScores === 'object') {
        // Check if savedScores has a nested 'scores' property (incorrect structure from parent)
        if (savedScores.scores && typeof savedScores.scores === 'object') {
          console.warn('[TemplateScoreModal] Detected nested scores structure, extracting...');
          initialScores = savedScores.scores;
        } else {
          // Use savedScores directly (correct structure)
          initialScores = savedScores;
        }
      }

      console.log('[TemplateScoreModal] Initialized scores:', initialScores);
      setScores(initialScores);

      // Initialize NA flags for items with null or "N/A" scores
      const initialNaFlags = {};
      Object.keys(initialScores).forEach((itemId) => {
        if (initialScores[itemId] === null || initialScores[itemId] === "N/A") {
          initialNaFlags[itemId] = true;
        }
      });
      setNaFlags(initialNaFlags);

      // Clear errors when opening
      setErrors({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]); // Only re-initialize when modal opens/closes, not when savedScores changes

  // Calculate total score and max possible score
  useEffect(() => {
    let total = 0;
    let maxTotal = 0;

    items.forEach((item) => {
      const itemId = item._id;

      // Only count if not marked as N/A
      if (!naFlags[itemId]) {
        // Use item.score as the max score (as per backend schema)
        maxTotal += item.score || 0;

        // Add the entered score (if any)
        const score = parseFloat(scores[itemId]) || 0;
        total += score;
      }
    });

    setTotalScore(total);
    setMaxPossibleScore(maxTotal);
  }, [scores, naFlags, items]);

  // Handle score change
  const handleScoreChange = (itemId, value) => {
    console.log('[TemplateScoreModal] Score change:', { itemId, value });

    setScores((prev) => {
      const newScores = {
        ...prev,
        [itemId]: value,
      };
      console.log('[TemplateScoreModal] Updated scores:', newScores);
      return newScores;
    });

    // Clear error for this item
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[itemId];
      return newErrors;
    });

    // Clear N/A flag if user enters a score
    if (value !== "" && naFlags[itemId]) {
      setNaFlags((prev) => {
        const newFlags = { ...prev };
        delete newFlags[itemId];
        return newFlags;
      });
    }
  };

  // Handle N/A checkbox change
  const handleNaChange = (itemId, checked) => {
    setNaFlags((prev) => ({
      ...prev,
      [itemId]: checked,
    }));

    if (checked) {
      // Clear score when marked as N/A
      setScores((prev) => ({
        ...prev,
        [itemId]: null,
      }));
      // Clear error
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[itemId];
        return newErrors;
      });
    }
  };

  // Validate scores
  const validateScores = () => {
    const newErrors = {};

    items.forEach((item) => {
      const itemId = item._id;
      const score = scores[itemId];
      const isNa = naFlags[itemId];

      // Skip validation if marked as N/A
      if (isNa) {
        return;
      }

      // Check if score is provided
      if (score === "" || score === null || score === undefined) {
        newErrors[itemId] = "Score is required (or mark as N/A)";
        return;
      }

      const numScore = parseFloat(score);

      // Check if valid number
      if (isNaN(numScore)) {
        newErrors[itemId] = "Score must be a valid number";
        return;
      }

      // Check if within range
      if (numScore < 0) {
        newErrors[itemId] = "Score cannot be negative";
        return;
      }

      // Use item.score as the max score (as per backend schema)
      const maxScore = item.score || 0;
      if (numScore > maxScore) {
        newErrors[itemId] = `Score cannot exceed ${maxScore}`;
        return;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle save draft
  const handleSaveDraft = () => {
    // No validation for draft, just save current state (null values indicate N/A)
    onSave && onSave(true, scores);
  };

  // Handle submit
  const handleSubmit = () => {
    if (validateScores()) {
      // Save scores (null values indicate N/A)
      onSave && onSave(false, scores);
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-5xl transform overflow-hidden rounded-2xl bg-white shadow-2xl transition-all">
                {/* Header */}
                <div
                  className={`px-6 py-5 ${
                    isCore
                      ? "bg-gradient-to-r from-blue-600 to-blue-700"
                      : "bg-gradient-to-r from-gray-600 to-gray-700"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-white bg-opacity-20 rounded-lg">
                        <DocumentTextIcon className="h-6 w-6 text-white" />
                      </div>
                      <div className="text-left">
                        <Dialog.Title
                          as="h3"
                          className="text-xl font-bold text-white"
                        >
                          {template?.name || "Template Scoring"}
                        </Dialog.Title>
                        <div className="flex items-center space-x-2 mt-1">
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${
                              isCore
                                ? "bg-blue-100 text-blue-800"
                                : "bg-gray-100 text-gray-700"
                            }`}
                          >
                            {isCore ? (
                              <AcademicCapIcon className="h-3 w-3 mr-1" />
                            ) : (
                              <BeakerIcon className="h-3 w-3 mr-1" />
                            )}
                            {isCore ? "Core" : "Supplementary"}
                          </span>
                          <span className="text-xs font-medium text-blue-100 bg-white bg-opacity-20 px-2 py-0.5 rounded-full">
                            {programmeTypeDisplay}
                          </span>
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={onClose}
                      disabled={isLoading}
                      className="rounded-lg p-1.5 text-white hover:bg-white hover:bg-opacity-20 transition-colors duration-200 disabled:opacity-50"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>

                {/* Total Score Display */}
                <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-medium text-gray-600">Total Score</h4>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Current score from {items.length} items
                      </p>
                    </div>
                    <div className="text-right">
                      <div className="text-3xl font-bold text-gray-900">
                        {totalScore.toFixed(2)}
                        <span className="text-lg text-gray-600">/{maxPossibleScore}</span>
                      </div>
                      <div className="text-sm font-medium text-gray-600 mt-0.5">
                        {maxPossibleScore > 0
                          ? ((totalScore / maxPossibleScore) * 100).toFixed(1)
                          : 0}
                        %
                      </div>
                    </div>
                  </div>
                </div>

                {/* Items List */}
                <div className="px-6 py-5 max-h-[70vh] overflow-y-auto bg-gray-50">
                  {items.length === 0 ? (
                    <div className="text-center py-12">
                      <ExclamationTriangleIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        No Items Available
                      </h3>
                      <p className="text-sm text-gray-600">
                        This template does not have any scoring items.
                      </p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      {items.map((item, index) => {
                        const itemId = item._id;
                        const isNa = naFlags[itemId];
                        const error = errors[itemId];
                        const hasScore = scores[itemId] && !isNa;
                        const isComplete = hasScore && !error;

                        return (
                          <div
                            key={itemId}
                            className={`rounded-xl border-2 transition-all shadow-sm hover:shadow-lg overflow-hidden ${
                              error
                                ? "border-red-400 bg-red-50"
                                : isComplete
                                ? "border-green-400 bg-gradient-to-br from-green-50 to-white"
                                : "border-red-400 bg-red-50"
                            }`}
                          >
                            {/* Card Header */}
                            <div className={`px-4 py-3 border-b-2 ${
                              error
                                ? "border-red-300 bg-red-500"
                                : isComplete
                                ? "border-green-300 bg-green-500"
                                : "border-red-300 bg-red-500"
                            }`}>
                              <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-2">
                                  <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold ${
                                    error
                                      ? "bg-white text-red-600"
                                      : isComplete
                                      ? "bg-white text-green-600"
                                      : "bg-white text-red-600"
                                  }`}>
                                    {index + 1}
                                  </span>
                                  <h4 className="text-sm font-bold text-white">
                                    {item.name}
                                  </h4>
                                </div>
                                {isComplete && (
                                  <CheckCircleIcon className="h-5 w-5 text-white" />
                                )}
                                {!isComplete && (
                                  <ExclamationTriangleIcon className="h-5 w-5 text-white" />
                                )}
                              </div>
                            </div>

                            {/* Card Body */}
                            <div className="p-4 space-y-3">
                              {/* Score Input */}
                              <div>
                                <ScoreInput
                                  name={`score-${itemId}`}
                                  value={isNa ? "" : scores[itemId] || ""}
                                  onChange={(value) => handleScoreChange(itemId, value)}
                                  maxScore={item.score}
                                  disabled={isNa || isLoading || isReadOnly}
                                  error={error}
                                  placeholder={isNa ? "Marked as N/A" : isReadOnly ? "Submitted - Read Only" : `Enter score (Max: ${item.score})`}
                                  showValidation={true}
                                />
                                {error && (
                                  <p className="mt-1 text-xs font-medium text-red-600 flex items-center">
                                    <ExclamationTriangleIcon className="h-3 w-3 mr-1" />
                                    {error}
                                  </p>
                                )}
                              </div>

                              {/* N/A Checkbox */}
                              <div className="pt-2 border-t border-gray-200">
                                <label className={`flex items-center space-x-2 ${isReadOnly ? 'cursor-default' : 'cursor-pointer'} group`}>
                                  <input
                                    type="checkbox"
                                    checked={isNa || false}
                                    onChange={(e) =>
                                      handleNaChange(itemId, e.target.checked)
                                    }
                                    disabled={isLoading || isReadOnly}
                                    className="w-4 h-4 text-gray-600 border-gray-300 rounded focus:ring-gray-500 disabled:opacity-50"
                                  />
                                  <span className="text-xs font-medium text-gray-600 group-hover:text-gray-800">
                                    Not Applicable (N/A)
                                  </span>
                                </label>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="bg-gray-50 px-6 py-4 flex items-center justify-end space-x-3 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={onClose}
                    disabled={isLoading}
                    className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border-2 border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isReadOnly ? 'Close' : 'Cancel'}
                  </button>
                  {!isReadOnly && (
                    <button
                      type="button"
                      onClick={handleSubmit}
                      disabled={isLoading}
                      className="px-5 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                    >
                      {isLoading ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          <span className="text-white">Submitting...</span>
                        </>
                      ) : (
                        <>
                          <CheckCircleIcon className="h-5 w-5 text-white" />
                          <span className="text-white">Submit</span>
                        </>
                      )}
                    </button>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default TemplateScoreModal;
